import React from 'react'
import PropTypes from 'prop-types'
import { CloseAlt } from '@ticketswap/comets'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { CHANGE_TICKET_ALERTS } from './changeTicketAlerts'
import { CREATE_TICKET_ALERT } from './createTicketAlert'
import { UPDATE_TICKET_ALERT } from './updateTicketAlert'
import { GET_SUPPORTED_CURRENCIES } from './getSupportedCurrencies'
import { GET_ACTIVE_TICKET_ALERTS } from './getActiveTicketAlerts'
import styled from '@emotion/styled'
import {
  useDialog,
  DialogWindow,
  DialogHeader,
  DialogAdornment,
  DialogBody,
  MoneyInput,
  Button,
  space,
  Alert,
} from '@ticketswap/solar'
import { decodeGlobalId } from '~/utils/id'

const StyledButton = styled(Button)`
  margin-top: ${space[16]};
`

const StyledAlert = styled(Alert)`
  margin-top: ${space[16]};
`

function PriceLimitDialog({
  t,
  alerts,
  types,
  eventId,
  eventTypeId,
  onUpdate,
  children,
}) {
  const [error, setError] = React.useState(null)
  const [changeTicketAlerts, { loading: changing }] = useMutation(
    CHANGE_TICKET_ALERTS
  )
  const [createTicketAlert, { loading: creating }] = useMutation(
    CREATE_TICKET_ALERT
  )
  const [updateTicketAlert, { loading: updating }] = useMutation(
    UPDATE_TICKET_ALERT
  )
  const { hide, getToggleProps, getWindowProps } = useDialog()
  const { data = {} } = useQuery(GET_SUPPORTED_CURRENCIES)
  const limit = React.useRef(getInitialPriceLimit())
  const currencies =
    data?.supportedCurrencies?.supportedCurrencies ||
    new Array({
      code: 'EUR',
      symbol: '€',
      name: 'Euro',
    })

  /**
   * Check if the array contains an alert for the current event-type
   * ID and return its maximumPrice value if defined.
   */
  function getInitialPriceLimit() {
    const event = ({ event }) => event.id === eventId
    const eventType = ({ eventType }) => eventType?.id === eventTypeId

    if (alerts.filter(eventType).length > 0) {
      const maximumPrice = alerts.filter(eventType)[0]?.maximumPrice

      if (!maximumPrice) return null

      return {
        amount: maximumPrice.amount,
        currency: maximumPrice.currency,
      }
    }

    if (alerts.filter(event).length > 1) {
      const maximumPrice = alerts.filter(event)[0]?.maximumPrice

      if (!maximumPrice) return null

      return {
        amount: maximumPrice.amount,
        currency: maximumPrice.currency,
      }
    }

    return null
  }

  /**
   * Check the `alerts` array for the current event-type and return
   * its ID.
   */
  function getTicketAlertIdForCurrentEventType() {
    const eventType = ({ eventType }) => eventType?.id === eventTypeId

    if (alerts.filter(eventType).length > 0) {
      return alerts.filter(eventType)[0]?.id
    }

    return null
  }

  /**
   * Returns the currency that should initially be selected upon
   * opening the dialog.
   */
  function getInitialSelectedCurrency() {
    return (
      currencies.filter(
        currency => currency.code === limit.current?.currency
      )[0] || currencies[0]
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    // Filter
    const eventWithoutEventType = ({ event, eventType }) =>
      event.id === eventId && !eventType

    // Check if we have an event-level alert defined
    if (alerts.filter(eventWithoutEventType).length > 0) {
      let changeTicketAlertsResponse = null

      // If YES, remove it by calling `changeTicketAlerts` and
      // activate all types but the current one, then add a new alert
      // with a price limit using the old `createTicketAlert`
      // mutation.
      await changeTicketAlerts({
        variables: {
          input: {
            eventTypesAlert: [
              ...types.map(({ node }) => ({
                eventTypeId: node.id,
                active: node.id === eventTypeId ? false : true,
              })),
            ],
          },
        },
        update(_, { data: { changeTicketAlerts } }) {
          // Store the response in a temporary variable because we’ll
          // need it later in the update function of the
          // `createTicketAlert` mutation.
          changeTicketAlertsResponse = changeTicketAlerts.ticketAlerts
        },
      })

      // Because `changeTicketAlerts` does currently not support passing
      // a price limit we need to use the old mutation for it.
      const { data } = await createTicketAlert({
        variables: {
          input: {
            eventId,
            eventTypeId,
            maximumPrice: limit.current,
          },
        },
        update(cache, { data: { createTicketAlert } }) {
          const { viewer } = cache.readQuery({
            query: GET_ACTIVE_TICKET_ALERTS,
          })
          cache.writeQuery({
            query: GET_ACTIVE_TICKET_ALERTS,
            data: {
              viewer: {
                ...viewer,
                activeTicketAlerts: [
                  ...changeTicketAlertsResponse,
                  createTicketAlert.ticketAlert,
                ],
              },
            },
          })
        },
      })

      if (data?.createTicketAlert?.errors?.length > 0) {
        return setError(data.createTicketAlert.errors[0])
      }
    } else {
      // If NO, add a price limit to the alert for the current event
      // type by using the old `updateTicketAlert` mutation.
      const { data } = await updateTicketAlert({
        variables: {
          input: {
            ticketAlertId: getTicketAlertIdForCurrentEventType(),
            eventTypeId,
            // If the chosen limit is 0, reset the maximum price.
            ...(limit.current.amount !== 0 && { maximumPrice: limit.current }),
          },
        },
        update(cache, { data: { updateTicketAlert } }) {
          const { viewer } = cache.readQuery({
            query: GET_ACTIVE_TICKET_ALERTS,
          })
          cache.writeQuery({
            query: GET_ACTIVE_TICKET_ALERTS,
            data: {
              viewer: {
                ...viewer,
                activeTicketAlerts: [
                  ...viewer.activeTicketAlerts,
                  updateTicketAlert.ticketAlert,
                ],
              },
            },
          })
        },
      })

      if (data?.updateTicketAlert?.errors?.length > 0) {
        return setError(data.updateTicketAlert.errors[0])
      }
    }

    onUpdate({
      alertId: decodeGlobalId(getTicketAlertIdForCurrentEventType()),
      priceLimit: limit.current,
    })

    // Hide the dialog.
    hide()
  }

  return (
    <>
      {children({ getToggleProps })}
      <DialogWindow {...getWindowProps()}>
        <DialogHeader>
          {t('event-eventtype:ticketAlerts.priceLimitDialog.title')}
          <DialogAdornment right>
            <button onClick={hide}>
              <CloseAlt size={24} />
            </button>
          </DialogAdornment>
        </DialogHeader>
        <DialogBody>
          <form onSubmit={handleSubmit}>
            <MoneyInput
              id="price-limit"
              name="price-limit"
              label={t('event-eventtype:ticketAlerts.priceLimitDialog.title')}
              currencies={currencies}
              onChange={({ amount, currency }) => {
                limit.current = {
                  amount: amount > 0 ? amount : 0,
                  currency: currency.code,
                }
              }}
              initialSelectedCurrency={getInitialSelectedCurrency()}
              initialAmount={limit.current?.amount || 0}
              help={t('event-eventtype:ticketAlerts.priceLimitDialog.help')}
            />

            {error && <StyledAlert>{error.message}</StyledAlert>}

            <StyledButton
              type="submit"
              width="full"
              loading={changing || creating || updating}
            >
              {t('common:generic.save')}
            </StyledButton>
          </form>
        </DialogBody>
      </DialogWindow>
    </>
  )
}

PriceLimitDialog.defaultProps = {
  alerts: [],
  types: [],
}

PriceLimitDialog.propTypes = {
  t: PropTypes.func.isRequired,
  eventId: PropTypes.string.isRequired,
  eventTypeId: PropTypes.string,
  alerts: PropTypes.array,
  types: PropTypes.array,
  onUpdate: PropTypes.func.isRequired,
}

export default PriceLimitDialog
